/**
 * product-novariants.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ProductFamilyPicker — cross-product variant engine for the no-variants PDP.
 *
 * FETCH STRATEGY (mirrors beach-glow-soap-co/assets/product-section.js):
 *   Fetch the full product page:  GET /products/{handle}
 *   This returns a complete HTML document. Parse it with DOMParser, then
 *   query the section's outer Shopify wrapper:
 *     doc.querySelector('#shopify-section-{section.id}')
 *   All surgical replacements happen inside that wrapper — exactly like
 *   the beach-glow reference implementation.
 *
 *   We do NOT use the Section Rendering API (?section_id=...) because that
 *   returns only a bare fragment and the wrapper ID we need doesn't exist
 *   inside the fragment — it IS the fragment root, making nested queries fail.
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  const WRAPPER_CLASS  = 'nv-section-wrapper';
  const LOADING_CLASS  = 'nv-is-loading';
  const MATRIX_ELEM_ID = 'product-family-matrix';

  class ProductFamilyPicker {
    constructor() {
      const matrixEl = document.getElementById(MATRIX_ELEM_ID);
      if (!matrixEl) return;

      try {
        this.matrix = JSON.parse(matrixEl.textContent);
      } catch (e) {
        console.error('[ProductFamilyPicker] Failed to parse matrix:', e);
        return;
      }

      // The outer Shopify section wrapper: <div id="shopify-section-{id}">
      // This is what we query in fetched pages. e.g. "shopify-section-template--21234__main"
      this.sectionWrapperId = this.matrix.sectionWrapperId;

      // .nv-section-wrapper — our live-page scoping anchor
      this.wrapper = document.querySelector('.' + WRAPPER_CLASS);
      if (!this.wrapper) {
        const section = document.getElementById(this.sectionWrapperId);
        if (section) {
          this.wrapper = section.querySelector('.' + WRAPPER_CLASS);
        }
        if (!this.wrapper) return;
      }

      this.state = {
        size:     this.matrix.currentSize,
        flavorId: this.matrix.currentFlavorId
      };

      this.isFetching = false;
      this._bindEvents();

      // Sync initial state (thumbnails, disabled states, etc.)
      this._syncSelectorStates();

      // Seed the initial product into browser history so back button works
      history.replaceState(
        { productHandle: this.matrix.currentHandle },
        '',
        window.location.pathname + window.location.search
      );
    }

    // ── Event binding ────────────────────────────────────────────────────────
    // Listen on document so stopPropagation inside <product-rerender> can't block us

    _bindEvents() {
      document.addEventListener('change', (e) => {
        const input = e.target;

        if (input.name === 'nv-size') {
          // Add loading class to the clicked size label
          const label = this.wrapper.querySelector(`label[for="${input.id}"]`);
          if (label) {
            label.classList.add('loading');
          }
          
          this.state.size = input.value;
          this._resolveAndFetch();
        } else if (input.name === 'nv-flavor') {
          // Add loading class to the clicked flavor label
          const label = this.wrapper.querySelector(`label[for="${input.id}"]`);
          if (label) {
            label.classList.add('loading');
          }
          
          // Parse as number since matrix stores flavorId as numbers
          this.state.flavorId = parseInt(input.value, 10);
          this._resolveAndFetch();
        }
      });
    }

    // ── Handle resolution ────────────────────────────────────────────────────

    _resolveHandle() {
      const match = this.matrix.products.find(
        p => p.size === this.state.size && p.flavorId === this.state.flavorId
      );
      return match ? match.handle : null;
    }

    // ── Fetch + swap ─────────────────────────────────────────────────────────

    async _resolveAndFetch() {
      if (this.isFetching) return;

      const handle = this._resolveHandle();

      if (!handle) {
        console.warn('[ProductFamilyPicker] No product found for state:', this.state);
        return;
      }

      if (handle === this.matrix.currentHandle) {
        this._syncSelectorStates();
        return;
      }

      this.isFetching = true;
      this._setLoadingState(true);

      try {
        // Fetch the FULL product page — same pattern as beach-glow-soap-co
        const url = `/products/${handle}`;
        const response = await fetch(url, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);

        const html = await response.text();
        const doc  = new DOMParser().parseFromString(html, 'text/html');

        // Find the section's outer Shopify wrapper in the fetched full-page doc.
        // The fetched product will have a DIFFERENT section ID than the current one,
        // so we can't search by the exact ID. Instead, look for the main product section
        // pattern: shopify-section-template--*__main
        let newSection = null;
        
        // Try to find any section that matches the main product pattern
        const allSections = doc.querySelectorAll('[id^="shopify-section-template"]');
        
        for (const section of allSections) {
          if (section.id.includes('__main')) {
            newSection = section;
            break;
          }
        }

        if (!newSection) {
          console.error('[ProductFamilyPicker] Could not find main product section in fetched page for /products/' + handle);
          // Hard navigate as fallback — better than silent failure
          window.location.href = url;
          return;
        }

        this._updateDOM(newSection, handle, url);

      } catch (err) {
        console.error('[ProductFamilyPicker] Fetch error:', err);
      } finally {
        this.isFetching = false;
        this._setLoadingState(false);
      }
    }

    // ── DOM update ───────────────────────────────────────────────────────────

    _updateDOM(newSection, handle, url) {
      const live = this.wrapper;

      // Helper: replace a live element with its counterpart from the fetched section.
      // Returns the newly-inserted live element, or null.
      const swap = (selector) => {
        const src  = newSection.querySelector(selector);
        const dest = live.querySelector(selector);
        if (src && dest) {
          dest.replaceWith(src);
          return live.querySelector(selector);
        }
        return null;
      };

      // 1. Media gallery
      swap('product-gallery');

      // 2. Product title
      swap('.product-info__title');

      // 3. Price block
      swap('.product-info__price');

      // 4. Product description
      const descSwapped = swap('.product-info__description');
      if (!descSwapped) {
        // If direct swap failed, maybe description is in an accordion or different structure
        // Try swapping the entire description block
        const newDesc = newSection.querySelector('.product-info__description, [data-variant-description-app]');
        const liveDesc = live.querySelector('.product-info__description, [data-variant-description-app]');
        if (newDesc && liveDesc) {
          liveDesc.replaceWith(newDesc);
        }
      }

      // 5. Flavor FAQs
      swap('#variant-faqs-wrapper');

      // 6. Buy buttons + product form + subscription widget
      //    replaceWith() triggers connectedCallback on <purchase-options> so
      //    the Stay AI widget reinitialises with the new product's selling plans.
      const newBuy  = newSection.querySelector('.product-info__buy-buttons');
      const liveBuy = live.querySelector('.product-info__buy-buttons');
      
      if (newBuy && liveBuy) {
        liveBuy.replaceWith(newBuy);
        
        // Check for purchase-options element after swap
        const newPurchaseOptions = live.querySelector('.product-info__buy-buttons purchase-options');
        
        // Re-execute inline <script> tags — browsers skip scripts inserted via replaceWith
        this._rerunScripts(live.querySelector('.product-info__buy-buttons'));
        
        // Force purchase-options reinit by disconnecting and reconnecting
        if (newPurchaseOptions) {
          // Store parent and next sibling for reinsertion
          const parent = newPurchaseOptions.parentNode;
          const nextSibling = newPurchaseOptions.nextSibling;
          
          // Remove and reinsert to trigger connectedCallback again
          parent.removeChild(newPurchaseOptions);
          
          // Reinsert after a tick to ensure proper cleanup
          setTimeout(() => {
            parent.insertBefore(newPurchaseOptions, nextSibling);
          }, 10);
        }
      }

      // 7. Sticky ATC bar image + price
      swap('.product-quick-add__variant');

      // 8. Update our internal state tracker
      this.matrix.currentHandle = handle;

      // 9. Sync picker visual states (selected / disabled labels)
      this._syncSelectorStates();

      // 10. Push URL — "as if opened a separate product page"
      history.pushState({ productHandle: handle }, '', url);

      // 11. Custom event for analytics / wishlist apps
      document.dispatchEvent(new CustomEvent('product-family:changed', {
        bubbles: true,
        detail: { handle, size: this.state.size, flavorId: this.state.flavorId }
      }));

      // 12. Re-bind FAQ accordion click handlers on the freshly-swapped wrapper
      this._initFaqAccordion();

      // 13. Remove soldout class from .step-counter-2 if the current product is available
      this._updateStepCounterAvailability();
    }

    // ── Script re-execution ──────────────────────────────────────────────────

    _rerunScripts(container) {
      if (!container) return;
      container.querySelectorAll('script').forEach((old) => {
        const fresh = document.createElement('script');
        Array.from(old.attributes).forEach(a => fresh.setAttribute(a.name, a.value));
        fresh.textContent = old.textContent;
        old.parentNode.replaceChild(fresh, old);
      });
    }

    // ── Selector state sync ──────────────────────────────────────────────────

    _syncSelectorStates() {
      const w = this.wrapper;

      w.querySelectorAll('input[data-selector="size"]').forEach((input) => {
        const label = w.querySelector(`label[for="${input.id}"]`);
        if (!label) return;
        const isActive = input.value === this.state.size;
        input.checked = isActive;
        label.classList.toggle('is-selected', isActive);
        
        // Remove loading class when is-selected is applied
        if (isActive) {
          label.classList.remove('loading');
        }
        
        const hasCombo = this.matrix.products.some(
          p => p.size === input.value && p.flavorId === this.state.flavorId && p.available
        );
        label.classList.toggle('is-disabled', !hasCombo);
        input.disabled = !hasCombo;

        // Update size thumbnail to match current flavor
        const img = label.querySelector('img');
        if (img) {
          const matchingProduct = this.matrix.products.find(
            p => p.size === input.value && p.flavorId === this.state.flavorId
          );
          if (matchingProduct && matchingProduct.imageUrl) {
            img.src = matchingProduct.imageUrl;
            img.alt = input.value;
          }
          
          // Update serving number
          const servingEl = label.querySelector('.js-serving');
          if (servingEl && matchingProduct && matchingProduct.serving) {
            servingEl.textContent = matchingProduct.serving  + " servings";
          }
        }
      });

      w.querySelectorAll('input[data-selector="flavor"]').forEach((input) => {
        const label = w.querySelector(`label[for="${input.id}"]`);
        if (!label) return;
        // Convert input.value to number for comparison with state.flavorId (which is a number)
        const inputFlavorId = parseInt(input.value, 10);
        const isActive = inputFlavorId === this.state.flavorId;
        input.checked = isActive;
        label.classList.toggle('is-selected', isActive);
        
        // Remove loading class when is-selected is applied
        if (isActive) {
          label.classList.remove('loading');
        }
        
        const hasCombo = this.matrix.products.some(
          p => p.flavorId === inputFlavorId && p.size === this.state.size && p.available
        );
        label.classList.toggle('is-disabled', !hasCombo);
        input.disabled = !hasCombo;
      });
    }

    // ── Step counter availability update ─────────────────────────────────────

    _updateStepCounterAvailability() {
      const stepCounter2 = document.querySelector('.step-counter-2');
      if (!stepCounter2) return;

      // Find the current product in the matrix
      const currentProduct = this.matrix.products.find(
        p => p.size === this.state.size && p.flavorId === this.state.flavorId
      );

      // Remove soldout class if product is available
      if (currentProduct && currentProduct.available) {
        stepCounter2.classList.remove('soldout');
      } else {
        stepCounter2.classList.add('soldout');
      }
    }

    // ── FAQ accordion re-init ────────────────────────────────────────────────

    _initFaqAccordion() {
      const faqWrapper = this.wrapper.querySelector('#variant-faqs-wrapper');
      if (!faqWrapper) return;
      const items = faqWrapper.querySelectorAll('#pdp-accordion li');
      if (!items.length) return;

      const closeAll = () => {
        items.forEach(li => {
          li.classList.remove('active', 'is-before-active');
          const d = li.querySelector('.description');
          if (d) d.style.height = '0px';
        });
      };

      const openItem = (item) => {
        item.classList.add('active');
        const prev = item.previousElementSibling;
        if (prev) prev.classList.add('is-before-active');
        const d = item.querySelector('.description');
        const inner = d && d.querySelector('div');
        if (d && inner) d.style.height = inner.scrollHeight + 'px';
      };

      items.forEach(item => {
        item.addEventListener('click', () => {
          const was = item.classList.contains('active');
          closeAll();
          if (!was) openItem(item);
        });
      });
    }

    // ── Loading state ────────────────────────────────────────────────────────

    _setLoadingState(on) {
      this.wrapper.classList.toggle(LOADING_CLASS, on);
      
      // Add variant-loading class to .product-info parent
      const productInfo = this.wrapper.querySelector('.product-info');
      if (productInfo) {
        productInfo.classList.toggle('variant-loading', on);
      }
    }
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────

  function boot() {
    const picker = new ProductFamilyPicker();

    // Back/forward button support
    window.addEventListener('popstate', (e) => {
      const handle = e.state && e.state.productHandle ? e.state.productHandle : null;
      if (!handle || !picker || !picker.matrix) return;

      const entry = picker.matrix.products.find(p => p.handle === handle);
      if (!entry) return;

      picker.state.size     = entry.size;
      picker.state.flavorId = entry.flavorId;

      if (picker.wrapper) {
        picker.wrapper.querySelectorAll('input[data-selector="size"]').forEach(i => {
          i.checked = i.value === entry.size;
        });
        picker.wrapper.querySelectorAll('input[data-selector="flavor"]').forEach(i => {
          i.checked = i.value === entry.flavorId;
        });
      }

      picker._resolveAndFetch();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
